import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { HostPortalContext } from "@/contexts/portalBranding";
import { Button } from "@/components/ui/button";
import { SpinnerBlock } from "@/components/ui/spinner";
import { PoweredByReveshare } from "@/components/common/PoweredByReveshare";
import { NotFound } from "@/pages/NotFound";
import { ProgramPageRenderer } from "@/components/programPage/ProgramPageRenderer";
import { programPageService, type ProgramPageBrand } from "@/services/programPage.service";
import { loginUrlFor } from "@/utils/returnUrl";

/**
 * `/program` on a brand's portal: the program page with no account needed.
 *
 * Like `/join`, it only means something where the subdomain says which brand
 * this is; on the marketplace apex it is an ordinary 404. A signed-in
 * affiliate is sent to the in-app copy instead, where the same page also
 * shows their progress.
 */
export function PortalProgramRoute() {
  const { branding } = useContext(HostPortalContext);
  const username = branding?.brandUsername;
  if (!username) return <NotFound />;
  if (localStorage.getItem("token")) {
    return <Navigate to={`/affiliate/program/${username}`} replace />;
  }
  return <PublicProgramPage username={username} />;
}

function PublicProgramPage({ username }: { username: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["program-page", "public", username],
    queryFn: () => programPageService.getPublic(username),
    retry: false,
  });

  if (isLoading) return <SpinnerBlock />;
  if (!data) return <NotFound />;

  const loginUrl = loginUrlFor(`/affiliate/program/${username}`);

  if (data.restricted) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <BrandBar brand={data.brand} loginUrl={loginUrl} />
        <div className="mx-auto flex max-w-md flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
          <Lock className="h-8 w-8 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">This page is for {data.brand.companyName ?? "our"} affiliates</h1>
          <p className="text-muted-foreground">Log in to see the program, or apply to join.</p>
          <div className="flex gap-3">
            <Button asChild>
              <Link to={loginUrl}>Log in</Link>
            </Button>
            <Button variant="outline" asChild>
              <a href={data.brand.joinUrl}>Apply to join</a>
            </Button>
          </div>
        </div>
        <PoweredByReveshare />
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen flex-col bg-background"
      style={{ background: data.document.theme.colors.background ?? undefined }}
    >
      <Helmet>
        <title>{`${data.navLabel} · ${data.brand.companyName ?? ""}`}</title>
      </Helmet>
      <BrandBar brand={data.brand} loginUrl={loginUrl} />
      <ProgramPageRenderer
        className="flex-1"
        document={data.document}
        programs={data.programs}
        defaultProgramId={data.defaultProgramId}
        joinUrl={data.brand.joinUrl}
      />
      <div className="bg-background">
        <PoweredByReveshare />
      </div>
    </div>
  );
}

function BrandBar({ brand, loginUrl }: { brand: ProgramPageBrand; loginUrl: string }) {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-border bg-card px-5 py-3">
      <div className="flex min-w-0 items-center gap-3">
        {brand.logo && <img src={brand.logo} alt="" className="h-8 w-8 rounded-md object-contain" />}
        <span className="truncate font-semibold">{brand.companyName}</span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to={loginUrl}>Log in</Link>
        </Button>
        <Button size="sm" asChild>
          <a href={brand.joinUrl}>Join</a>
        </Button>
      </div>
    </header>
  );
}
